// Open FD verification (legacy /AccountsApproval/VerifyOpenFD).
// Pending FDs are verified/approved via the shared status list (legacy drops "Return" from this
// page's dropdown); FDs returned earlier (Status '0') get legacy's resubmit form.
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { PiggyBank, Clock, Building2, RotateCcw, Send } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import {
    fetchVerifyOpenFDQueue,
    fetchVerifyOpenFDDetail,
    fetchOpenFDBanks,
    submitApproveOpenFD,
    submitUpdateOpenFD,
    clearVerifyOpenFDDetail,
    resetVerifyOpenFD,
    selectVOFQueue, selectVOFQueueLoading, selectVOFQueueError,
    selectVOFDetail, selectVOFDetailLoading, selectVOFDetailError,
    selectVOFBanks, selectVOFVerifyLoading, selectVOFUpdateLoading,
} from '../../slices/accountsSlice/verifyOpenFDSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

const ACTION_VERB = {
    Verify: 'Verified Successfully',
    Approve: 'Approved Successfully',
    Reject: 'Rejected Successfully',
};

// Legacy's FD payment-mode list has a single entry.
const PAY_MODES = ['RTGS/E-Trans'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const Field = ({ label, value, className = '' }) => (
    <div className={className}>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words">{value || value === 0 ? value : '—'}</p>
    </div>
);

const StatusBadge = ({ status }) => (String(status) === '0' ? (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">Return</span>
) : (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">Verification Pending</span>
));

const inputCls = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';
const labelCls = 'block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1';

const formatAmount = (v) => (v != null && v !== '' ? `₹ ${formatIndianCurrency(v)}` : null);
const todayIso = () => (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
const addDaysIso = (iso, days) => {
    const d = new Date(`${iso}T00:00:00`);
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; // local date (ISO is UTC)
};
// "25-Sep-2026" ⇄ "2026-09-25". The SP takes these dates as strings — keep legacy's dd-MMM-yyyy.
const legacyToIso = (v) => {
    const m = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(String(v || '').trim());
    if (!m) return '';
    const month = MONTHS.findIndex((x) => x.toLowerCase() === m[2].toLowerCase()) + 1;
    return month ? `${m[3]}-${String(month).padStart(2, '0')}-${m[1].padStart(2, '0')}` : '';
};
const isoToLegacy = (v) => {
    const [y, mo, d] = String(v || '').split('-');
    return y && mo && d ? `${d}-${MONTHS[Number(mo) - 1]}-${y}` : '';
};

const VerifyOpenFD = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    const queue = useSelector(selectVOFQueue);
    const queueLoading = useSelector(selectVOFQueueLoading);
    const queueError = useSelector(selectVOFQueueError);
    const detail = useSelector(selectVOFDetail);
    const detailLoading = useSelector(selectVOFDetailLoading);
    const detailError = useSelector(selectVOFDetailError);
    const banks = useSelector(selectVOFBanks);
    const verifyLoading = useSelector(selectVOFVerifyLoading);
    const updateLoading = useSelector(selectVOFUpdateLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [comment, setComment] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);
    // Returned-FD resubmit form
    const [upd, setUpd] = useState({ fromDate: '', toDate: '', roi: '', amount: '', bankId: '', mode: '', no: '', payDate: '', remarks: '' });
    const setField = (key, value) => setUpd((prev) => ({ ...prev, [key]: value }));

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    const isReturned = String(selectedItem?.Status) === '0';
    // Only trust the detail that belongs to the row currently selected.
    const fd = selectedItem && detail?.BankTransactionRefNo === selectedItem.BankTransactionRefNo ? detail : null;

    useEffect(() => {
        if (roleId) dispatch(fetchVerifyOpenFDQueue(roleId));
    }, [roleId, dispatch]);

    useEffect(() => {
        // Legacy removes "Return" from this page's status dropdown.
        dispatch(setShowReturnButton('No'));
        return () => {
            dispatch(resetVerifyOpenFD());
            dispatch(resetApprovalData());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!selectedItem) return;
        dispatch(clearVerifyOpenFDDetail());
        dispatch(resetApprovalData());
        dispatch(setShowReturnButton('No'));
        dispatch(fetchVerifyOpenFDDetail(selectedItem.BankTransactionRefNo));
        if (String(selectedItem.Status) === '0' && !banks.length) dispatch(fetchOpenFDBanks());
        setComment('');
        setIsLeftPanelCollapsed(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // The queue row carries no MOID; the detail does.
    useEffect(() => {
        if (isReturned || !fd?.MOID || !roleId) return;
        dispatch(fetchStatusList({ MOID: fd.MOID, ROID: roleId, ChkAmt: 0 }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fd?.MOID, fd?.BankTransactionRefNo]);

    // Prefill the resubmit form once the returned FD is in.
    useEffect(() => {
        if (!isReturned || !fd) return;
        setUpd({
            fromDate: legacyToIso(fd.ReturnFromDate),
            toDate: legacyToIso(fd.ReturnToDate),
            roi: fd.FDRROI != null ? String(fd.FDRROI) : '',
            amount: fd.FDRAmount != null ? String(fd.FDRAmount) : '',
            bankId: fd.Bankid ? String(fd.Bankid) : '',
            mode: PAY_MODES.includes(fd.ModeofPay) ? fd.ModeofPay : '',
            no: fd.No || '',
            payDate: legacyToIso(fd.ReturnPayDate),
            remarks: fd.Remarks || '',
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fd?.BankTransactionRefNo]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId) dispatch(fetchVerifyOpenFDQueue(roleId));
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchVerifyOpenFDQueue(roleId));
            setSelectedItem(null);
            setComment('');
            setIsLeftPanelCollapsed(false);
            dispatch(clearVerifyOpenFDDetail());
            dispatch(resetApprovalData());
        }, 800);
    };

    const handleActionClick = async (action) => {
        if (!fd) { toast.error('No FD selected'); return; }
        if (!comment.trim()) { toast.error('Enter Note'); return; }

        const actionValue = action.type;
        const payload = {
            BankTransactionRefNo: fd.BankTransactionRefNo,
            Action: actionValue,
            Roleid: roleId,
            ApprovalNote: comment.trim(),
            Createdby: getCurrentUser(),
            Bankid: fd.Bankid,
            PaymentAmount: fd.PaymentAmount,
            ReturnFromDate: fd.ReturnFromDate,
        };

        try {
            const result = await dispatch(submitApproveOpenFD(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status === 'Submitted' || status === 'Submited') {
                toast.success(`Open FD ${ACTION_VERB[actionValue] || `${actionValue}d`}`);
                finishAndRefresh();
                return;
            }
            // spApproveOpenFD reports budget / bank balance problems through the same status string.
            toast.error(status || 'Error Occurred');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred');
        }
    };

    const decimalChange = (key) => (e) => {
        const value = e.target.value;
        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) setField(key, value);
    };

    const handleResubmit = async () => {
        if (!fd) return;
        const errors = [];
        if (!fd.FDRNo) errors.push('Enter FD No');
        if (!upd.fromDate) errors.push('Select From Date');
        if (!upd.toDate) errors.push('Select To Date');
        if (upd.fromDate && upd.toDate && upd.toDate < upd.fromDate) errors.push('To Date must be on or after From Date');
        if (upd.roi === '') errors.push('Enter Rate of Interest');
        if (!upd.amount || parseFloat(upd.amount) === 0) errors.push('Enter FD Amount');
        if (!upd.bankId) errors.push('Select Bank');
        if (!upd.mode) errors.push('Select Mode of Pay');
        if (!upd.no.trim()) errors.push('Enter No');
        if (!upd.payDate) errors.push('Select Payment Date');
        if (!upd.remarks.trim()) errors.push('Enter Remarks');
        if (errors.length) { errors.forEach((m) => toast.error(m)); return; }

        const payload = {
            FDRNo: fd.FDRNo,
            FDRFromDate: isoToLegacy(upd.fromDate),
            FDRClosingDate: isoToLegacy(upd.toDate),
            FDRAmount: parseFloat(upd.amount),
            FDRROI: parseFloat(upd.roi),
            Createdby: getCurrentUser(),
            Bankid: upd.bankId,
            Remarks: upd.remarks.trim(),
            ModeofPay: upd.mode,
            No: upd.no.trim(),
            PaymentDate: isoToLegacy(upd.payDate),
            Action: 'Update',
            Roleid: roleId,
            BankTransactionRefNo: fd.BankTransactionRefNo,
        };

        try {
            const result = await dispatch(submitUpdateOpenFD(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status === 'Updated') {
                toast.success('Open FD Updated Successfully');
                finishAndRefresh();
                return;
            }
            toast.error(status || 'Error Occurred While Updating OpenFD');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Updating OpenFD');
        }
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return String(item.FDRNo ?? '').toLowerCase().includes(q)
            || item.ReturnFromDate?.toLowerCase().includes(q)
            || item.ReturnToDate?.toLowerCase().includes(q)
            || String(item.FDRAmount ?? '').includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <PiggyBank className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">FD {item.FDRNo}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.ReturnFromDate} → {item.ReturnToDate}</p>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">{formatAmount(item.FDRAmount)}</span>
            </div>
            <div className="flex justify-end"><StatusBadge status={item.Status} /></div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[140px]">FD {item.FDRNo}</span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[200px]">{item.ReturnFromDate} → {item.ReturnToDate}</span>
            <span className="font-semibold text-gray-900 dark:text-white">{formatAmount(item.FDRAmount)}</span>
            <span className="ml-auto"><StatusBadge status={item.Status} /></span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <PiggyBank className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderPendingDetails = () => (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="FD Number" value={fd.FDRNo} />
                <Field label="Begin Date" value={fd.ReturnFromDate} />
                <Field label="End Date" value={fd.ReturnToDate} />
                <Field label="Rate of Interest (%)" value={fd.FDRROI} />
                <Field label="FD Value" value={formatAmount(fd.FDRAmount)} />
                <Field label="Bank Name" value={fd.BankName} />
                <Field label="Transaction Number" value={fd.No} />
                <Field label="Transaction Date" value={fd.ReturnPayDate} />
                <Field label="Transaction Amount" value={formatAmount(fd.PaymentAmount)} />
                {fd.Remarks && <Field label="Remarks" value={fd.Remarks} className="sm:col-span-2 lg:col-span-3" />}
            </div>
        </div>
    );

    const renderResubmitForm = () => (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-5 border-2 border-amber-200 dark:border-amber-700 space-y-4">
            <div className="flex items-center gap-2">
                <RotateCcw className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">Returned for Update — correct and resubmit</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="FD Number" value={fd.FDRNo} />
                <div>
                    <label className={labelCls}>Begin Date <span className="text-red-500">*</span></label>
                    <input type="date" className={inputCls} value={upd.fromDate} max={todayIso()} onChange={(e) => setField('fromDate', e.target.value)} />
                </div>
                <div>
                    <label className={labelCls}>End Date <span className="text-red-500">*</span></label>
                    <input type="date" className={inputCls} value={upd.toDate} min={upd.fromDate || undefined} max={addDaysIso(todayIso(), 1825)} onChange={(e) => setField('toDate', e.target.value)} />
                </div>
                <div>
                    <label className={labelCls}>Rate of Interest (%) <span className="text-red-500">*</span></label>
                    <input type="text" inputMode="decimal" className={inputCls} value={upd.roi} onChange={decimalChange('roi')} />
                </div>
                <div>
                    <label className={labelCls}>FD Amount <span className="text-red-500">*</span></label>
                    <input type="text" inputMode="decimal" className={inputCls} value={upd.amount} onChange={decimalChange('amount')} />
                </div>
                <div>
                    <label className={labelCls}>Bank Name <span className="text-red-500">*</span></label>
                    <select className={inputCls} value={upd.bankId} onChange={(e) => setField('bankId', e.target.value)}>
                        <option value="">-Please Select-</option>
                        {banks.map((b) => <option key={b.Bank_Id} value={b.Bank_Id}>{b.Bank_Name}</option>)}
                    </select>
                </div>
                <div>
                    <label className={labelCls}>Mode of Pay <span className="text-red-500">*</span></label>
                    <select className={inputCls} value={upd.mode} onChange={(e) => setField('mode', e.target.value)}>
                        <option value="">-Please Select-</option>
                        {PAY_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                </div>
                <div>
                    <label className={labelCls}>No <span className="text-red-500">*</span></label>
                    <input type="text" className={inputCls} value={upd.no} onChange={(e) => setField('no', e.target.value)} />
                </div>
                <div>
                    <label className={labelCls}>Payment Date <span className="text-red-500">*</span></label>
                    <input type="date" className={inputCls} value={upd.payDate} min={upd.fromDate || undefined} max={todayIso()} onChange={(e) => setField('payDate', e.target.value)} />
                </div>
                <Field label="Payment Amount" value={formatAmount(upd.amount)} />
                <div className="sm:col-span-3">
                    <label className={labelCls}>Remarks <span className="text-red-500">*</span></label>
                    <textarea rows={3} className={inputCls} value={upd.remarks} onChange={(e) => setField('remarks', e.target.value)} />
                </div>
            </div>
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={handleResubmit}
                    disabled={updateLoading}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                    {updateLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Send className="w-4 h-4" />}
                    Resubmit
                </button>
            </div>
        </div>
    );

    const renderActions = () => {
        if (statusLoading) {
            return (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                    </div>
                </div>
            );
        }
        if (statusError) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                </div>
            );
        }
        if (!hasActions || !enabledActions || enabledActions.length === 0) {
            return (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                    <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this FD</p>
                </div>
            );
        }
        return (
            <ActionButtons
                actions={enabledActions}
                onActionClick={handleActionClick}
                loading={verifyLoading}
                isVerified={true}
                comment={comment}
                showValidation={true}
            />
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        if (detailLoading || (!fd && !detailError)) {
            return (
                <div className="flex items-center justify-center gap-3 py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading FD details…</span>
                </div>
            );
        }
        if (detailError || !fd) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ {detailError || 'FD details could not be loaded — it may no longer be pending at your level.'}</p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">FD {fd.FDRNo}</h2>
                    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">{formatAmount(fd.FDRAmount)}</p>
                    <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> {fd.BankName}
                        </span>
                        <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">{fd.ReturnFromDate} → {fd.ReturnToDate}</span>
                        {fd.FDRROI != null && (
                            <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">{fd.FDRROI}% p.a.</span>
                        )}
                        <StatusBadge status={selectedItem.Status} />
                    </div>
                </div>

                {isReturned ? renderResubmitForm() : (
                    <>
                        {renderPendingDetails()}
                        <SimpleCommentInput
                            comment={comment}
                            onCommentChange={(e) => setComment(e.target.value)}
                            label="Notes"
                            placeholder="Enter verification notes…"
                            required
                            rows={3}
                            showCharCount
                        />
                        {renderActions()}
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'Open FD Verification'} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={PiggyBank}
                badgeText="Open FD Verification"
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by FD number, dates, amount…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: 'No FDs pending verification!',
                        itemKey: 'BankTransactionRefNo',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'Open FD Verification',
                        icon: PiggyBank,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'FD Details',
                        icon: PiggyBank,
                        selectedTitle: 'Open FD Verification',
                        emptyTitle: 'No FD Selected',
                        emptyMessage: 'Select an FD from the list to view details and take action.',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyOpenFD;
